import React, { FC, memo } from 'react';
import { useTranslate } from '@/translations';
import { faClose, Icon } from '../Icon';
import { TitleWrapper } from './PreviewModal.styled';
import { downloadFile } from '@/utils/helpers';
import { StyledLink, StyledLinkContainer } from '@/ui/globalStyles';
import {
  ImageSupportedExtensions,
  XLSXSupportedExtensions,
} from '@/utils/constants';
import { Button, Image, Modal } from 'antd';
import { PDFReader } from '../PdfReader';
import { ExcelReader } from '../ExcelReader';
import { NoPreview } from './NoPreview';
import { PreviewModalProps } from './types';

const getFileExtension = (fileName: string): string =>
  fileName.split('.').pop() || '';

const getComponentToRenderByExtension = (
  extension: string,
  src: string,
  closeModal: () => void,
) => {
  switch (true) {
    case extension === 'pdf':
      return () => <PDFReader src={src} />;
    case XLSXSupportedExtensions.includes(extension):
      return () => <ExcelReader src={src} />;
    default:
      // if extension is undefined or not handled
      return () => <NoPreview closeModal={closeModal} />;
  }
};

export const PreviewModal: FC<PreviewModalProps> = memo(
  ({ isOpen, closeModal, src, fileName }) => {
    const { t } = useTranslate();

    const extension = getFileExtension(fileName);

    if (ImageSupportedExtensions.includes(extension))
      return (
        <Image
          width={10}
          style={{ display: 'none' }}
          preview={{
            visible: isOpen,
            src,
            onVisibleChange: () => closeModal(),
          }}
        />
      );

    const ComponentToRender = getComponentToRenderByExtension(
      extension,
      src,
      closeModal,
    );

    return (
      <Modal
        open={isOpen}
        onCancel={closeModal}
        title={
          <TitleWrapper>
            <Button onClick={() => downloadFile(src, fileName)}>
              {t('download')}
            </Button>
            <StyledLinkContainer onClick={closeModal}>
              <StyledLink>
                <Icon icon={faClose} scale={2} />
              </StyledLink>
            </StyledLinkContainer>
          </TitleWrapper>
        }
        centered
        footer={null}
      >
        <ComponentToRender />
      </Modal>
    );
  },
);
